import { Dialog } from '@bloomfieldrobotics/canopy';
import { DialogContent, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { LoadingListSkeleton } from '../../../../../sprouts/components/LoadingListSkeleton/LoadingListSkeleton';
import { StrictDateRange } from '../../../../commonUI/DateRangeFilterSelect/types';
import { RawCountsBlockTable } from './components/RawCountsBlockTable';
import { RawCountsDateSection } from './components/RawCountsDateSection';
import { useGroupedRawCounts } from './hooks/useGroupedRawCounts';

type Props = {
  open: boolean;
  handleClose: () => void;
  customerId: string;
  dateRange: StrictDateRange | undefined;
};
export const RawCountsDialog = ({
  open,
  handleClose,
  customerId,
  dateRange,
}: Props) => {
  const { groupedRawCounts, isLoading } = useGroupedRawCounts(
    customerId,
    dateRange
  );

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});

  const handleSectionExpand = (scanDate: string, expanded: boolean) => {
    setExpandedSections((prev) => ({
      ...prev,
      [scanDate]: expanded,
    }));
  };

  // Initialize first section as expanded when data loads
  useEffect(() => {
    if (
      groupedRawCounts.length > 0 &&
      Object.keys(expandedSections).length === 0
    ) {
      setExpandedSections({ [groupedRawCounts[0].scanDate]: true });
    }
  }, [groupedRawCounts, expandedSections]);

  return (
    <Dialog
      open={open}
      title='Raw Counts'
      maxWidth='xl'
      fullWidth
      onClose={handleClose}
    >
      <DialogContent>
        <LoadingListSkeleton length='long' loading={isLoading}>
          {groupedRawCounts.length > 0 ? (
            <Stack sx={{ gap: 2, width: '100%' }}>
              {groupedRawCounts.map((dateGroup, dateIndex) => (
                <RawCountsDateSection
                  key={dateGroup.scanDate}
                  displayDate={dateGroup.displayDate}
                  expanded={
                    expandedSections[dateGroup.scanDate] ?? dateIndex === 0
                  }
                  onExpandedChange={(expanded) =>
                    handleSectionExpand(dateGroup.scanDate, expanded)
                  }
                >
                  {dateGroup.blocks.map((block) => (
                    <RawCountsBlockTable
                      key={block.blockId}
                      blockName={block.blockName}
                      stages={block.stages}
                    />
                  ))}
                </RawCountsDateSection>
              ))}
            </Stack>
          ) : (
            <Typography variant='body2' sx={{ textAlign: 'center', py: 4 }}>
              No raw counts data available for the selected date range.
            </Typography>
          )}
        </LoadingListSkeleton>
      </DialogContent>
    </Dialog>
  );
};
